import type { ControllerBindingFields, ControllerKind } from "../Processing/types";
import type { ControlPanelPatch } from "../widgetContract";
import { effectiveBinding } from "../Processing/controllerBindings";
import { trainingCountForPreset } from "../AddWidgetModal/constants";

/**
 * True when this filter section follows a Slicer. The bulk apply skips it —
 * the controller owns that value now, and overwriting it would be silently undone.
 *
 * KNOWN GAP: a stat tile or player notes bound to a slicer on a session window is NOT
 * following its dates — the engine declines that substitution — but this still reads as
 * bound, so the bulk apply skips it. Closing that means `ControlPanelPatch.controllers`
 * carrying each slicer's state rather than its uuid alone.
 */
export function isBound(
  misc: ControllerBindingFields | undefined,
  kind: ControllerKind,
  patch: ControlPanelPatch,
): boolean {
  return effectiveBinding(misc, kind, patch.controllers[kind]) != null;
}

// The Control Panel collects one date-range preset + one subject selection and bulk-
// applies them across widgets. Widget-level builder-misc stores the preset under
// `dateRange`; per-series (chart/pie) builder-misc uses `dateRangePreset` and disables
// the inherit flag. These two mappers turn the raw patch into the right shape so each
// per-widget handler stays a one-liner.

/**
 * Narrow a start-date type for a widget whose builder misc cannot hold `latest_n`.
 *
 * The date form is shared by every widget, so its type carries the window. A stat tile
 * and player notes never offer it, so reading anything else as relative narrows the type
 * without changing behaviour. The bulk filters modal cannot produce one either, which
 * makes this a type guard rather than a runtime one.
 */
export function calendarDateType(type?: string): "relative" | "absolute" {
  return type === "absolute" ? "absolute" : "relative";
}

/** `widgetDatePatch` for a widget whose misc holds calendar dates only.
 *
 *  A training-count preset is dropped as well as the type: `buildDateFilter` reads the
 *  preset BEFORE the type, so one left here would emit a session window the widget's own
 *  schema then refuses, failing the save of the whole dashboard. */
export function calendarWidgetDatePatch(date: ControlPanelPatch["date"]) {
  const patch = widgetDatePatch(date);
  return {
    ...patch,
    startDateType: calendarDateType(date.startDateType),
    ...(trainingCountForPreset(patch.dateRange) != null && { dateRange: undefined }),
  };
}

/** Date fields for a widget-level builder-misc (`dateRange`). */
export function widgetDatePatch(date: ControlPanelPatch["date"]) {
  return {
    dateRange: date.dateRange,
    showAdvancedDate: date.showAdvancedDate,
    startDateType: date.startDateType,
    startDateAbsolute: date.startDateAbsolute,
    startDateOffset: date.startDateOffset,
    endDateType: date.endDateType,
    endDateAbsolute: date.endDateAbsolute,
    endDateOffset: date.endDateOffset,
    dayFilter: date.dayFilter,
  };
}

/** Date fields for a per-series builder-misc (`dateRangePreset` + inherit off). */
export function seriesDatePatch(date: ControlPanelPatch["date"]) {
  return {
    dateRangePreset: date.dateRange,
    showAdvancedDate: date.showAdvancedDate,
    startDateType: date.startDateType,
    startDateAbsolute: date.startDateAbsolute,
    startDateOffset: date.startDateOffset,
    endDateType: date.endDateType,
    endDateAbsolute: date.endDateAbsolute,
    endDateOffset: date.endDateOffset,
    dayFilter: date.dayFilter,
    inheritDateRange: false,
  };
}
